/**
 * Display formatting for money and time.
 *
 * The API speaks rupees as decimal strings — "1499.50". These helpers parse
 * that text straight into integer paisa and format from there, so a value
 * crosses the wire and reaches the screen without ever touching IEEE 754.
 * `parseFloat("1499.50")` would be the obvious shortcut and the wrong one.
 */

const SYMBOLS: Record<string, string> = { INR: '₹', USD: '$', EUR: '€', GBP: '£' };

/** Rupees as written ("1499.50", "1499", 1499.5) into integer paisa. */
export function toPaisa(value: string | number | bigint | null | undefined): bigint {
  if (value === null || value === undefined) return 0n;
  if (typeof value === 'bigint') return value * 100n;

  const text = typeof value === 'number' ? String(value) : value.trim();
  const match = /^(-?)(\d+)(?:\.(\d{1,2}))?$/.exec(text);
  if (!match) return 0n;

  const [, sign, whole, fraction = ''] = match;
  const paisa = BigInt(whole!) * 100n + BigInt(fraction.padEnd(2, '0') || '0');
  return sign === '-' ? -paisa : paisa;
}

/** Full precision, Indian grouping for INR. Takes rupees, e.g. "1499.50". */
export function formatAmount(value: string | number | bigint | null | undefined, currency = 'INR'): string {
  const minor = toPaisa(value);
  const negative = minor < 0n;
  const abs = negative ? -minor : minor;
  const whole = (abs / 100n).toString();
  const paise = (abs % 100n).toString().padStart(2, '0');

  const grouped =
    currency === 'INR'
      ? whole.replace(/(\d)(?=(\d\d)+\d$)/g, '$1,')
      : whole.replace(/\B(?=(\d{3})+(?!\d))/g, ',');

  return `${negative ? '−' : ''}${SYMBOLS[currency] ?? currency + ' '}${grouped}.${paise}`;
}

/**
 * Compact form for KPI tiles, using Indian units — a finance team in Bengaluru
 * reads "₹4.2 Cr" instantly and "₹42M" not at all.
 */
export function formatCompact(value: string | number | bigint | null | undefined, currency = 'INR'): string {
  const minor = toPaisa(value);
  const rupees = Number(minor / 100n);
  const symbol = SYMBOLS[currency] ?? '';

  if (currency !== 'INR') {
    if (rupees >= 1e9) return `${symbol}${(rupees / 1e9).toFixed(2)}B`;
    if (rupees >= 1e6) return `${symbol}${(rupees / 1e6).toFixed(2)}M`;
    if (rupees >= 1e3) return `${symbol}${(rupees / 1e3).toFixed(1)}K`;
    return `${symbol}${rupees.toFixed(2)}`;
  }

  if (rupees >= 1e7) return `${symbol}${(rupees / 1e7).toFixed(2)} Cr`;
  if (rupees >= 1e5) return `${symbol}${(rupees / 1e5).toFixed(2)} L`;
  if (rupees >= 1e3) return `${symbol}${(rupees / 1e3).toFixed(1)}K`;
  return formatAmount(rupees.toFixed(2), currency);
}

/* -------------------------------------------------------------------------
 *  Enum labels
 *
 *  `UPI_INTENT` lowercased and CSS-capitalized becomes "Upi Intent". The
 *  acronyms in this domain — UPI, EMI, IMPS, NEFT, RTGS, UTR, GST — all break
 *  that way, and a payments product that spells UPI "Upi" looks like it was
 *  assembled by someone who has never used one.
 * ----------------------------------------------------------------------- */

const ACRONYMS = new Set(['UPI', 'EMI', 'IMPS', 'NEFT', 'RTGS', 'UTR', 'GST', 'KYC', 'MDR', 'USDT', 'API', 'QR']);

/** Turns an enum value into a display label: `UPI_INTENT` → "UPI Intent". */
export function enumLabel(value: string | null | undefined): string {
  if (!value) return '—';
  return value
    .split(/[_\s]+/)
    .filter(Boolean)
    .map((word) => {
      const upper = word.toUpperCase();
      if (ACRONYMS.has(upper)) return upper;
      return upper.charAt(0) + word.slice(1).toLowerCase();
    })
    .join(' ');
}

export function relativeTime(input: string | Date | null | undefined): string {
  if (!input) return '—';
  const date = typeof input === 'string' ? new Date(input) : input;
  const seconds = Math.round((Date.now() - date.getTime()) / 1000);

  if (seconds < 45) return 'just now';
  if (seconds < 3600) return `${Math.round(seconds / 60)}m ago`;
  if (seconds < 86_400) return `${Math.round(seconds / 3600)}h ago`;
  if (seconds < 604_800) return `${Math.round(seconds / 86_400)}d ago`;

  return date.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
}
